import React, { createContext, useContext, useState, PropsWithChildren } from "react";
import axios from "axios";

export interface DeckertPost {
  id: string;
  company: string;
  category: string;
  title: string;
  description: string;
  createdAt: string;
  updatedAt: string;
}

export interface DeckertTeamMember {
  id: string;
  company: string;
  name: string;
  role: string;
  profileImageUrl: string | null;
  location: string;
  bio: string;
  quote: string | null;
  createdAt: string;
  updatedAt: string;
}

interface CmsContextType {
  posts: DeckertPost[];
  teamMembers: DeckertTeamMember[];
  fetchPosts: (company: string) => Promise<DeckertPost[]>;
  fetchTeamMembers: (company: string) => Promise<DeckertTeamMember[]>;
  createPost: (data: Partial<DeckertPost>) => Promise<DeckertPost | null>;
  updatePost: (id: string, data: Partial<DeckertPost>) => Promise<DeckertPost | null>;
  deletePost: (id: string, company: string) => Promise<void>;
  createMember: (data: Partial<DeckertTeamMember>) => Promise<DeckertTeamMember | null>;
  updateMember: (id: string, data: Partial<DeckertTeamMember>) => Promise<DeckertTeamMember | null>;
  deleteMember: (id: string, company: string) => Promise<void>;
  uploadMemberImage: (id: string, file: File) => Promise<DeckertTeamMember | null>;
  removeMemberImage: (id: string) => Promise<boolean>;
}

const CmsContext = createContext<CmsContextType>({} as CmsContextType);

export const CmsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [posts, setPosts] = useState<DeckertPost[]>([]);
  const [teamMembers, setTeamMembers] = useState<DeckertTeamMember[]>([]);

  const getToken = () => localStorage.getItem("authToken");

  const fetchPosts = async (company: string): Promise<DeckertPost[]> => {
    try {
      const res = await axios.get("/api/deckert/cms/posts", {
        headers: { Authorization: `Bearer ${getToken()}` },
        params: { company },
      });
      // Backend liefert ENTWEDER ein Array ODER ein Objekt mit items (siehe Punkt 2)
      const items: DeckertPost[] = Array.isArray(res.data) ? res.data : res.data?.items ?? [];
      setPosts(items);
      return items;
    } catch (error) {
      console.error("Fehler beim Laden der Posts:", error);
      setPosts([]); // defensiv
      return [];
    }
  };

  const fetchTeamMembers = async (company: string): Promise<DeckertTeamMember[]> => {
    try {
      const res = await axios.get("/api/deckert/cms/team-members", {
        headers: { Authorization: `Bearer ${getToken()}` },
        params: { company },
      });
      const items: DeckertTeamMember[] = Array.isArray(res.data) ? res.data : res.data?.items ?? [];
      setTeamMembers(items);
      return items;
    } catch (error) {
      console.error("Fehler beim Laden der Team-Mitglieder:", error);
      setTeamMembers([]);
      return [];
    }
  };

  const createPost = async (data: Partial<DeckertPost>): Promise<DeckertPost | null> => {
    try {
      const res = await axios.post("/api/deckert/cms/posts", data, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      return res.data.post;
    } catch (error) {
      console.error("Fehler beim Erstellen des Posts:", error);
      return null;
    }
  };

  const updatePost = async (id: string, data: Partial<DeckertPost>): Promise<DeckertPost | null> => {
    try {
      const res = await axios.put(`/api/deckert/cms/posts/${id}`, data, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      return res.data.post;
    } catch (error) {
      console.error("Fehler beim Aktualisieren des Posts:", error);
      return null;
    }
  };

  const deletePost = async (id: string, company: string) => {
    try {
      await axios.delete(`/api/deckert/cms/posts/${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        params: { company },
      });
    } catch (error) {
      console.error("Fehler beim Löschen des Posts:", error);
    }
  };

  const createMember = async (data: Partial<DeckertTeamMember>): Promise<DeckertTeamMember | null> => {
    try {
      const res = await axios.post("/api/deckert/cms/team-members", data, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      return res.data.member;
    } catch (error) {
      console.error("Fehler beim Erstellen des Team-Mitglieds:", error);
      return null;
    }
  };

  const updateMember = async (id: string, data: Partial<DeckertTeamMember>): Promise<DeckertTeamMember | null> => {
    try {
      const res = await axios.put(`/api/deckert/cms/team-members/${id}`, data, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      return res.data.member;
    } catch (error) {
      console.error("Fehler beim Aktualisieren des Team-Mitglieds:", error);
      return null;
    }
  };

  const uploadMemberImage = async (id: string, file: File): Promise<DeckertTeamMember | null> => {
    try {
      const form = new FormData();
      form.append("imageFile", file, file.name);

      const token = getToken();
      const res = await axios.post(`/api/deckert/cms/team-members/${id}/upload-image`, form, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "multipart/form-data",
        },
        transformRequest: [(data) => data],
      });

      if (res.data?.member) return res.data.member as DeckertTeamMember;

      if (res.data?.profileImageUrl) {
        return await updateMember(id, { profileImageUrl: res.data.profileImageUrl });
      }

      return null;
    } catch (err: any) {
      console.error("uploadMemberImage failed:", err);
      return null;
    }
  };

  const removeMemberImage = async (id: string): Promise<boolean> => {
    try {
      await axios.delete(`/api/deckert/cms/team-members/${id}/image`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      return true;
    } catch (e) {
      console.error("removeMemberImage failed:", e);
      return false;
    }
  };

  const deleteMember = async (id: string, company: string) => {
    try {
      await axios.delete(`/api/deckert/cms/team-members/${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        params: { company },
      });
    } catch (error) {
      console.error("Fehler beim Löschen des Team-Mitglieds:", error);
    }
  };

  return (
    <CmsContext.Provider
      value={{
        posts,
        teamMembers,
        fetchPosts,
        fetchTeamMembers,
        createPost,
        updatePost,
        deletePost,
        createMember,
        updateMember,
        deleteMember,
        uploadMemberImage,
        removeMemberImage,
      }}>
      {children}
    </CmsContext.Provider>
  );
};

export const useCms = () => useContext(CmsContext);
